/**
 * 코치 패널 — §14 "우측 코치 피드 = [제안]/[신호]/[요지]/[불일치]/[미답]
 * 타임스탬프 항목". 호스트 전용(§8.2 vis=host).
 */

import { Badge } from '@cupia/ui/badge'
import { Empty } from '@cupia/ui/empty'
import { Alert, AlertDescription } from '@cupia/ui/alert'
import { Lightbulb, PauseCircle } from 'lucide-react'
import type { BadgeProps } from '@cupia/ui/badge'
import type { CoachItem, RoomView } from '../lib/reducer'
import type { FeedOrder } from '../lib/feed-order'
import type { CoachKind } from '../lib/events'

const KIND_VARIANT: Record<CoachKind, NonNullable<BadgeProps['variant']>> = {
  제안: 'soft',
  신호: 'softInfo',
  요지: 'softGray',
  불일치: 'softDanger',
  미답: 'softWarn',
}

function clock(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
}

export function CoachPanel({
  items,
  room,
  enabled,
  order = 'newest-first',
}: {
  items: CoachItem[]
  room: RoomView
  enabled: boolean
  /**
   * 자막 피드와 **같은 정렬**을 쓴다(§14.1). 두 열이 서로 반대 방향으로
   * 흐르면 회의 중에 눈이 어지럽다. 별도 토글을 두지 않고 자막의 취향을
   * 그대로 따른다 — 하나의 설정에 두 개의 진실을 만들지 않는다.
   */
  order?: FeedOrder
}): React.ReactElement {
  // §7.3 L1 — 적체 시 코치 잡은 전면 중단된다. 그 사실을 화면에 말해 준다.
  const gated = room.level >= 1

  return (
    <div className="flex h-full flex-col gap-3">
      {!enabled && (
        <Alert variant="default">
          <PauseCircle className="h-4 w-4" />
          <AlertDescription>코치 피드가 꺼져 있습니다 (설정에서 켤 수 있습니다).</AlertDescription>
        </Alert>
      )}

      {enabled && gated && (
        <Alert variant="warning">
          <PauseCircle className="h-4 w-4" />
          <AlertDescription>
            적체로 코치 분석을 일시 중단했습니다 — 통역이 우선입니다.
          </AlertDescription>
        </Alert>
      )}

      {items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Empty
            icon={<Lightbulb className="h-6 w-6" />}
            title="아직 코치 항목이 없습니다"
            description="대화가 쌓이면 제안·신호·요지·불일치·미답을 정리해 드립니다."
          />
        </div>
      ) : (
        <ul className="flex flex-1 flex-col gap-2 overflow-y-auto">
          {/* 최신이 위로 — 코치는 자막과 달리 훑어보는 피드다 */}
          {(order === 'newest-first' ? [...items].reverse() : items).map((item) => (
            <li
              key={item.id}
              className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-3"
            >
              <div className="mb-1.5 flex items-center gap-2">
                <Badge variant={KIND_VARIANT[item.data.kind]}>{item.data.kind}</Badge>
                <span className="font-mono text-(length:--text-xs-size) tabular-nums text-[var(--color-muted-foreground)]">
                  {clock(item.ts)}
                </span>
              </div>
              <p className="text-(length:--text-sm-size) leading-relaxed">{item.data.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
